import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  Star, MapPin, Clock, Users, Euro, MessageSquare, 
  Calendar, ChevronLeft, Heart, Share2, Check, Award, ImageIcon
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_COLORS = {
  pro: "#4A90D9",
  elite: "#9B59B6",
  master: "#F1C40F",
  grandmaster: "#E74C3C",
  legendary: "#E07A5F"
};

const RANK_BADGES = {
  pro: "⭐",
  elite: "⭐⭐",
  master: "⭐⭐⭐",
  grandmaster: "⭐⭐⭐⭐",
  legendary: "👑"
};

export default function ProviderDetail() {
  const { providerId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const { t, language } = useLanguage();
  
  const [provider, setProvider] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  
  // Booking form state
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState('10:00');
  const [duration, setDuration] = useState(2);
  const [numPersons, setNumPersons] = useState(1);
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    const fetchProvider = async () => {
      try {
        const [providerRes, reviewsRes] = await Promise.all([
          fetch(`${API_URL}/api/providers/${providerId}`),
          fetch(`${API_URL}/api/providers/${providerId}/reviews`)
        ]);
        
        if (providerRes.ok) {
          const providerData = await providerRes.json();
          setProvider(providerData);
        } else {
          navigate('/providers');
        }
        
        if (reviewsRes.ok) {
          const reviewsData = await reviewsRes.json();
          setReviews(reviewsData);
        }
      } catch (error) {
        console.error('Error fetching provider:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProvider();
  }, [providerId, navigate]);

  const handleBooking = async () => {
    if (!isAuthenticated) {
      toast.error(t('common.error'));
      navigate('/login', { state: { from: { pathname: `/providers/${providerId}` } } });
      return;
    }
    
    if (!selectedDate || !address || !phone) {
      toast.error(t('common.error'));
      return;
    }

    setBookingLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          provider_id: providerId,
          date: selectedDate.toISOString().split('T')[0],
          start_time: startTime,
          duration_hours: duration,
          num_people: numPersons,
          capacity_needed: numPersons,
          address,
          phone,
          notes,
        }),
      });

      if (response.ok) {
        const booking = await response.json();
        toast.success(t('common.success'));
        setBookingOpen(false);
        navigate(`/bookings/${booking.booking_id}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common.error'));
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setBookingLoading(false);
    }
  };

  const clientHourlyRate = provider ? (provider.client_hourly_rate || provider.hourly_rate) : 0;
  const pricePerPerson = provider ? (provider.per_person_rate || 0) : 0;
  const billingMode = provider?.pricing_type || 'hourly';
  const isPerPersonBilling = billingMode === 'per_person' && pricePerPerson > 0;
  
  // Calculate total price based on billing mode
  // Per person: price per person × number of persons (NOT multiplied by hours)
  // Hourly: hourly rate × duration
  const totalPrice = provider ? (
    isPerPersonBilling 
      ? pricePerPerson * numPersons           // Per person pricing (flat rate per person)
      : clientHourlyRate * duration           // Hourly pricing
  ) : 0;

  const timeSlots = [];
  for (let h = 8; h <= 20; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
  }

  const getCategoryName = (category) => {
    return t(`categories.${category}`) || category;
  };

  const getRankName = (rank) => {
    return t(`ranks.${rank}`) || rank;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="aspect-video rounded-2xl skeleton" />
              <div className="h-8 w-64 skeleton" />
              <div className="h-4 w-full skeleton" />
            </div>
            <div className="h-96 rounded-2xl skeleton" />
          </div>
        </div>
      </div>
    );
  }

  if (!provider) return null;

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="provider-detail-page">
      <div className="container-app">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/providers')}
          data-testid="back-to-providers"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          {t('common.back')}
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Gallery */}
            <div className="space-y-4">
              <div className="aspect-video rounded-2xl overflow-hidden bg-muted relative">
                <img
                  src={provider.profile_image || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800'}
                  alt={provider.name}
                  className="w-full h-full object-cover"
                />
                {/* Rank Badge */}
                <div 
                  className="absolute top-4 left-4 px-4 py-2 rounded-full text-white font-semibold flex items-center gap-2"
                  style={{ backgroundColor: RANK_COLORS[provider.rank] }}
                >
                  <Award className="w-5 h-5" />
                  <span>{RANK_BADGES[provider.rank]} {getRankName(provider.rank)}</span>
                </div>
              </div>
            </div>

            {/* Provider Info */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <Badge className="mb-2 bg-accent/10 text-accent border-0">
                    {getCategoryName(provider.service_category)}
                  </Badge>
                  <h1 className="font-heading text-3xl font-semibold mb-2">{provider.name}</h1>
                  <div className="flex items-center gap-4 text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {provider.location}
                    </span>
                    <span className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      {provider.rating.toFixed(1)} ({provider.review_count} {t('provider.reviews')})
                    </span>
                    <span className="flex items-center gap-1">
                      <Check className="w-4 h-4 text-success" />
                      {provider.completed_missions} {t('provider.missions')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon">
                    <Heart className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon">
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <p className="text-foreground leading-relaxed mb-6">{provider.bio}</p>

              {/* Tags */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">{t('provider.specialties')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {provider.specialties?.map((specialty, index) => (
                      <Badge key={index} variant="secondary">
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">{t('provider.languages')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {provider.languages?.map((lang, index) => (
                      <Badge key={index} variant="outline">
                        {lang.toUpperCase()}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>

              {/* Portfolio Link */}
              <Button 
                variant="outline" 
                className="w-full sm:w-auto mt-4"
                onClick={() => navigate(`/providers/${providerId}/portfolio`)}
                data-testid="view-portfolio-btn"
              >
                <ImageIcon className="w-4 h-4 mr-2" />
                {t('blog.viewPosts')}
              </Button>
            </div>

            <Separator />

            {/* Details */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              <div className="text-center p-4 rounded-xl bg-muted/50">
                <Euro className="w-6 h-6 mx-auto mb-2 text-accent" />
                {isPerPersonBilling ? (
                  <>
                    <p className="font-semibold">{pricePerPerson.toFixed(2)}€/{t('provider.perPerson')}</p>
                    <p className="text-xs text-muted-foreground">{t('provider.perPersonPricing')}</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">{clientHourlyRate.toFixed(2)}€/h</p>
                    <p className="text-xs text-muted-foreground">{t('provider.perHour')}</p>
                  </>
                )}
              </div>
              <div className="text-center p-4 rounded-xl bg-muted/50">
                <Clock className="w-6 h-6 mx-auto mb-2 text-accent" />
                <p className="font-semibold">{t('provider.minHours')} {provider.min_hours}h</p>
                <p className="text-xs text-muted-foreground">{t('booking.duration')}</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-muted/50">
                <Users className="w-6 h-6 mx-auto mb-2 text-accent" />
                <p className="font-semibold">{t('provider.maxCapacity')} {provider.max_capacity}</p>
                <p className="text-xs text-muted-foreground">{t('booking.capacity')}</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-muted/50">
                <Check className="w-6 h-6 mx-auto mb-2 text-success" />
                <p className="font-semibold">{provider.is_available ? t('provider.available') : t('provider.unavailable')}</p>
                <p className="text-xs text-muted-foreground">Status</p>
              </div>
            </div>

            <Separator />

            {/* Reviews */}
            <div>
              <h2 className="font-heading text-2xl font-semibold mb-6">
                {t('provider.reviews')} ({reviews.length})
              </h2>
              
              {reviews.length === 0 ? (
                <p className="text-muted-foreground">{t('review.noReviews')}</p>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.review_id} className="flex gap-4">
                      <Avatar>
                        <AvatarFallback>{review.client_name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium">{review.client_name}</p>
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3.5 h-3.5 ${
                                  i < review.rating
                                    ? 'fill-amber-400 text-amber-400'
                                    : 'text-muted-foreground/30'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm">{review.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Booking Widget */}
          <div className="lg:col-span-1">
            <div className="booking-widget" data-testid="booking-widget">
              <div className="text-center mb-6">
                {isPerPersonBilling ? (
                  <>
                    <p className="text-3xl font-heading font-semibold">
                      {pricePerPerson.toFixed(2)}€<span className="text-lg text-muted-foreground font-normal">/{t('provider.perPerson')}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('provider.perPersonPricing')}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-3xl font-heading font-semibold">
                      {clientHourlyRate.toFixed(2)}€<span className="text-lg text-muted-foreground font-normal">{t('provider.perHour')}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('invoice.priceIncludesCommission')}
                    </p>
                  </>
                )}
              </div>

              <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary w-full mb-4" data-testid="book-provider-btn">
                    <Calendar className="w-4 h-4 mr-2" />
                    {t('provider.bookNow')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading">{t('booking.createBooking')} - {provider.name}</DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-6 py-4">
                    <div>
                      <Label className="mb-2 block">{t('booking.date')} *</Label>
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={(date) => date < new Date()}
                        className="rounded-md border"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="mb-2 block">{t('booking.time')} *</Label>
                        <Select value={startTime} onValueChange={setStartTime}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {timeSlots.map((time) => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="mb-2 block">{t('booking.duration')} *</Label>
                        <Select value={duration.toString()} onValueChange={(v) => setDuration(parseInt(v))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[...Array(8)].map((_, i) => {
                              const hours = i + provider.min_hours;
                              return (
                                <SelectItem key={hours} value={hours.toString()}>
                                  {hours} {t('common.hours')}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label className="mb-2 block">{t('booking.capacity')} *</Label>
                      <Select value={numPersons.toString()} onValueChange={(v) => setNumPersons(parseInt(v))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[...Array(provider.max_capacity)].map((_, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString()}>
                              {i + 1} {i === 0 ? t('common.person') : t('common.people')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="mb-2 block">{t('booking.address')} *</Label>
                      <Input
                        placeholder="123 Hauptstraße, 20095 Hamburg"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                    </div>

                    <div>
                      <Label className="mb-2 block">{t('booking.phone')} *</Label>
                      <Input
                        type="tel"
                        placeholder="+49 40 123 456"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>

                    <div>
                      <Label className="mb-2 block">{t('booking.notes')}</Label>
                      <Textarea
                        placeholder="..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      />
                    </div>

                    <div className="bg-muted/50 rounded-xl p-4">
                      <div className="flex justify-between mb-2 text-muted-foreground">
                        {isPerPersonBilling ? (
                          <span>{pricePerPerson.toFixed(2)}€ x {numPersons} {numPersons > 1 ? t('common.people') : t('common.person')}</span>
                        ) : (
                          <span>{clientHourlyRate.toFixed(2)}€ x {duration} {t('common.hours')}</span>
                        )}
                        <span>{totalPrice.toFixed(2)}€</span>
                      </div>
                      <Separator className="my-2" />
                      <div className="flex justify-between font-semibold">
                        <span>{t('booking.total')}</span>
                        <span data-testid="booking-total-price">{totalPrice.toFixed(2)}€</span>
                      </div>
                    </div>

                    <Button
                      className="btn-primary w-full"
                      onClick={handleBooking}
                      disabled={bookingLoading}
                      data-testid="confirm-booking-btn"
                    >
                      {bookingLoading ? t('common.loading') : t('booking.confirm')}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  if (!isAuthenticated) {
                    toast.error(t('common.error'));
                    navigate('/login');
                    return;
                  }
                  navigate(`/messages?provider=${provider.user_id}`);
                }}
                data-testid="message-provider-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                {t('provider.contactProvider')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
